// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// mui
import { Box, Button, Stack, Typography } from '@mui/material';

// components
import Loading from '@karehero/llama/components/pages/Loading/Loading';
import { OrganizationEditorPage } from '@karehero/llama/pwa/pages/OrganizationEditorPage/OrganizationEditorPage';
import { CopyText } from '@karehero/llama/components/atoms/CopyText/CopyText';

// editor
import { createFieldHelper } from '@karehero/llama/components/organisms/EditorFields/fieldHelper';

// store
import { FieldType, Organization, OrganizationType } from '@karehero/models';
import {
  useCreateOrganizationMutation,
  useGetAllOrganizationTypeQuery,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
} from 'store/api/organization';
import { newToast } from 'store/slices/uiSlice';
import { useDispatch } from 'react-redux';

// qr code
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

/**
 * OrganizationEditor is the dashboard page.
 */
export const OrganizationEditor = () => {
  // ref
  const qrCodeCanvasRef = useRef<HTMLCanvasElement>(null);

  // state
  const [organization, setOrganization] = useState<Partial<Organization>>({});

  // hooks
  const dispatch = useDispatch();
  const { organizationID } = useParams();
  const { data: organizationTypes } = useGetAllOrganizationTypeQuery();
  const goBack = useGoBack();
  const [createOrganization] = useCreateOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const { data: existingOrganization, isLoading } = useGetOrganizationQuery(
    organizationID || '',
    {
      skip: !organizationID,
    },
  );
  // memos
  const inviteKeyRegistrationURL = useMemo(
    () => `${window.location.origin}/register/organization/${organization.id}`,
    [organization],
  );

  const isEditing = useMemo<boolean>(() => !!organizationID, [organizationID]);

  const fields = useMemo(() => {
    return createFieldHelper<Organization>({
      name: {
        label: 'Name',
        field: {
          type: FieldType.Text,
        },
      },
      organizationTypeId: {
        label: 'Type',
        field: {
          type: FieldType.Select,
          options: organizationTypes?.map((type: OrganizationType) => ({
            value: type.id,
            label: type.name,
          })),
        },
      },
      organizationDomains: {
        label: 'Email domains',
        description:
          'Allowed email domains that users can use to register with this organisation.',
        field: {
          type: FieldType.MultiSelectBox,
          isCustom: true,
          options: [],
        },
        validation: (value: any) =>
          typeof value === 'string' &&
          !value.includes('@') &&
          value.length >= 3,
      },
      typeformID: {
        label: 'Typeform ID',
        description:
          'The Typeform ID used for onboarding members of this organisation.',
        field: {
          type: FieldType.Text,
        },
      },
      isCareAssessmentEnabled: {
        label: 'Care Assessment',
        description:
          'When disabled, the care assessment feature is hidden for members of this organisation.',
        field: {
          type: FieldType.Switch,
        },
      },
      isBenefitsCalculatorEnabled: {
        label: 'Benefits Calculator',
        description:
          'When disabled, the benefits eligibility calculator is hidden for members of this organisation.',
        field: {
          type: FieldType.Switch,
        },
      },
      isPoaEnabled: {
        label: 'Power of Attorney',
        description:
          'When disabled, the Power of Attorney feature is hidden for members of this organisation.',
        field: {
          type: FieldType.Switch,
        },
      },
      ...(existingOrganization?.partnerId
        ? {
            partnerName: {
              label: 'Referring partner',
              description:
                'Organisation is linked to this referring partner (uneditable here; unlink from referring partner table).',
              field: { type: FieldType.Text },
              isDisabled: true,
            },
          }
        : {}),
      isInviteKeyEnabled: {
        label: 'Invite Key',
        description:
          'Allows invite keys to be used instead of email (e.g. Payroll ID).',
        field: {
          type: FieldType.Switch,
        },
      },
      isInviteCode: {
        label: 'Invite Code',
        description:
          'Modifies invite emails sent to use a 6 digit code instead of a magic link.',
        field: {
          type: FieldType.Switch,
        },
      },
      isPrivateEmail: {
        label: 'Private Emails',
        isDisabled: existingOrganization?.isPrivateEmail,
        description:
          'Ensures all email used for invites matching the defined email domains are hashed and made private. Once enabled, this property can not be disabled.',
        field: {
          type: FieldType.Switch,
        },
      },
      ...(organization.isInviteKeyEnabled
        ? {
            isEmailRegistrationEnabled: {
              label: 'Email Registration',
              description:
                'Shows the email registration field on the organisation specific registration screen.',
              field: {
                type: FieldType.Switch,
              },
            },
            inviteKeyName: {
              label: 'Invite Key Name',
              description:
                'The name given to the invite key (e.g. Payroll ID, Employee Number)',
              placeholder: 'Payroll ID',
              field: {
                type: FieldType.Text,
              },
            },
          }
        : {}),
    });
  }, [organization, organizationTypes, existingOrganization]);

  const computedOrganization = useMemo(() => {
    return {
      ...organization,
      inviteKeyName: organization.isInviteKeyEnabled
        ? organization.inviteKeyName
        : undefined,
      organizationDomains: JSON.stringify(
        organization?.organizationDomains?.map((domain) => domain?.domain),
      ),
    };
  }, [organization]);

  const isSaveDisabled = useMemo(() => {
    // Check whether Invite Key config is valid
    if (
      organization.isInviteKeyEnabled &&
      (organization.inviteKeyName || '').length === 0
    ) {
      return true;
    }

    // Check whether org domains have changed
    const orgDomains = JSON.stringify(
      organization.organizationDomains?.map((domain) => domain.domain) || '[]',
    );
    const existingDomains = JSON.stringify(
      (existingOrganization?.organizationDomains || []).map(
        (domain) => domain.domain,
      ),
    );
    if (orgDomains !== existingDomains) {
      return false;
    }

    // Check whether the rest of org has changed
    return (
      JSON.stringify({ ...organization, organizationDomains: [] }) ===
      JSON.stringify({ ...existingOrganization, organizationDomains: [] })
    );
  }, [organization, existingOrganization]);

  // methods
  const handleSetOrganization = useCallback(
    (org: Partial<Organization> & { organizationDomains: string }) => {
      let computedOrg: any = {
        ...org,
        inviteKeyName: org.isInviteKeyEnabled ? org.inviteKeyName : undefined,
        organizationDomains:
          org.organizationDomains &&
          JSON.parse(org.organizationDomains).map((domain: string) => ({
            domain: domain,
          })),
      };

      computedOrg = Object.fromEntries(
        Object.entries(computedOrg).filter(([_, value]) => value !== undefined),
      );

      setOrganization((prev) => ({
        ...prev,
        ...computedOrg,
      }));
    },
    [setOrganization],
  );

  const handleSave = useCallback(async () => {
    organization.organizationType = organizationTypes?.find(
      (type) => type.id === organization.organizationTypeId,
    );
    isEditing
      ? updateOrganization(organization as Organization)
      : createOrganization(organization as Organization);

    goBack();
  }, [
    goBack,
    createOrganization,
    updateOrganization,
    organization,
    isEditing,
    organizationTypes,
  ]);

  const handleOnCopy = useCallback(() => {
    dispatch(
      newToast({
        description: 'Invite key registration URL copied to clipboard',
        variant: 'success',
      }),
    );
  }, [dispatch]);

  const handleCopyQRCode = useCallback(() => {
    if (!qrCodeCanvasRef.current) return;

    qrCodeCanvasRef.current.toBlob((blob) => {
      if (!blob) return;

      navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob,
        }),
      ]);
    });

    dispatch(
      newToast({
        description: 'Invite key registration QR code copied to clipboard',
        variant: 'success',
      }),
    );
  }, [dispatch]);

  // effects
  useEffect(() => {
    if (!existingOrganization) return;
    setOrganization(existingOrganization);
  }, [existingOrganization]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <OrganizationEditorPage
      title={isEditing ? 'Modify Organisation' : 'New Organisation'}
      fields={fields}
      value={computedOrganization}
      onChange={handleSetOrganization}
      onBack={goBack}
      onSave={handleSave}
      saveLabel={isEditing ? 'Save' : 'Create'}
      isSaveDisabled={isSaveDisabled}
      inviteKeySlot={
        organization.isInviteKeyEnabled ? (
          <Stack spacing={2}>
            <Typography variant='subtitle2'>
              Invite Key Registration URL
            </Typography>
            <CopyText value={inviteKeyRegistrationURL} onCopy={handleOnCopy} />
            <Stack
              spacing={1}
              sx={{
                alignItems: 'center',
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
              }}
            >
              <QRCodeSVG
                value={inviteKeyRegistrationURL}
                size={196}
                marginSize={4}
              />
              <Button
                variant='outlined'
                size='small'
                onClick={handleCopyQRCode}
              >
                Copy QR Code
              </Button>
              <QRCodeCanvas
                ref={qrCodeCanvasRef}
                value={inviteKeyRegistrationURL}
                size={1024}
                marginSize={4}
                style={{ display: 'none' }}
              />
            </Stack>
          </Stack>
        ) : undefined
      }
    />
  );
};

export default OrganizationEditor;
